import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { expensesApi, projectsApi, vendorsApi, unwrap, unwrapList } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useDayLock } from "../hooks/useDayLock";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { useBuilderQuery } from "../hooks/useBuilderQuery";
import { Button, Empty, Field, Icon, Input, Loader, Modal } from "../components/ui";
import { ConfirmDialog, LockedNote, RecordActions } from "../components/actions";
import { ExpenseForm, emptyExpenseForm, expensePayload, expenseToForm } from "../components/forms";
import { KhataSummary } from "../components/KhataSummary";
import { PaymentFields, ProofLine } from "../components/PaymentFields";
import { COPY } from "../utils/copy";
import { emptyKhata, emptyProof, paymentLabel, withProof } from "../utils/finance";
import { formatDate, formatMoney } from "../utils/format";

export default function VendorUdhar() {
  const { id } = useParams();
  const { isBuilder, query } = useBuilderQuery();
  const toast = useToast();
  const { isClosed, isTodayClosed, reload: reloadDays } = useDayLock();
  const { busy, run } = useAsyncAction();
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", note: "", ...emptyProof() });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyExpenseForm);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [udharRes, projectRes, vendorRes] = await Promise.all([
        vendorsApi.udharExpenses(id, query),
        isBuilder ? projectsApi.list({ limit: 100 }) : Promise.resolve({ data: { items: [] } }),
        vendorsApi.list({ ...query, limit: 100 }),
      ]);
      setData(unwrap(udharRes));
      setProjects(unwrapList(projectRes).items);
      setVendors(unwrapList(vendorRes).items);
    } catch (error) {
      toast.error(error.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, query.builderId]);

  const openPay = (item) => {
    setPaying(item);
    setPayForm({ amount: String(item.remainingAmount || ""), note: "", ...emptyProof() });
  };

  const settle = async (event) => {
    event.preventDefault();
    if (!paying) return;
    const body = withProof({ amount: Number(payForm.amount), note: payForm.note || "" }, payForm);
    const result = await run(() => expensesApi.settle(paying.id, body), "Udhar paid on this expense.");
    if (result) {
      setPaying(null);
      reloadDays();
      load();
    }
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editing) return;
    const result = await run(
      () => expensesApi.update(editing.id, expensePayload(form, { editing: true })),
      "Expense updated. Daily book updated too."
    );
    if (result) {
      setEditing(null);
      setForm(emptyExpenseForm);
      reloadDays();
      load();
    }
  };

  const remove = async () => {
    if (!pendingDelete) return;
    const result = await run(
      () => expensesApi.remove(pendingDelete.id),
      "Expense deleted. Money went back to the book."
    );
    if (result) {
      setPendingDelete(null);
      reloadDays();
      load();
    }
  };

  if (loading) return <Loader />;
  if (!data) return <Empty title="Vendor not found" text="Go back to Credits." />;

  const vendor = data.vendor || {};
  const khata = data.khata || emptyKhata();
  const items = data.items || [];

  return (
    <div className="stack">
      <Link to="/vendors/udhar" className="back-link">
        <Icon name="back" size={18} /> Back to Credits
      </Link>

      <header className="page-head">
        <div>
          <p className="eyebrow">Credit purchases</p>
          <h1>{vendor.name}</h1>
          <p className="muted">{vendor.phone || "No phone"}</p>
        </div>
        <div className="head-actions">
          <Link className="btn btn-ghost btn-sm" to={`/vendors/${id}`}>
            Full khata
          </Link>
          <Link className="btn btn-ghost btn-sm" to={`/vendors/${id}/statement`}>
            Statement
          </Link>
        </div>
      </header>

      <KhataSummary khata={khata} />

      {items.length === 0 ? (
        <Empty title="No credit bills" text="Credit purchases from this vendor will show here." />
      ) : (
        <div className="sale-grid">
          {items.map((item) => {
            const locked = isClosed(item.date);
            return (
              <article key={item.id} className="sale-card credit-card">
                <div className="sale-card-top">
                  <h3>{item.title}</h3>
                  <span className="pill pill-credit">Due {formatMoney(item.remainingAmount)}</span>
                </div>
                <p>
                  {formatDate(item.date)}
                  {item.project?.name ? ` · ${item.project.name}` : ""}
                  {item.note ? ` · ${item.note}` : ""}
                  {locked ? " · Closed day" : ""}
                </p>
                <div className="sale-meta">
                  <div>
                    <span>Bill</span>
                    <b>{formatMoney(item.amount)}</b>
                  </div>
                  <div>
                    <span>Paid</span>
                    <b>{formatMoney(item.paidAmount)}</b>
                  </div>
                  <div>
                    <span>Due</span>
                    <b>{formatMoney(item.remainingAmount)}</b>
                  </div>
                </div>
                <ProofLine
                  paymentType={item.paymentType}
                  referenceNumber={item.referenceNumber}
                  image={item.image}
                  bank={item.bank}
                />
                {(item.settlements || []).map((payment) => (
                  <span key={payment.id} className="proof-line">
                    Paid {formatMoney(payment.amount)} on {formatDate(payment.date)}
                    {payment.paymentType ? ` · ${paymentLabel(payment.paymentType)}` : ""}
                    {payment.bank?.name ? ` · ${payment.bank.name}` : ""}
                    {payment.referenceNumber ? ` · ${payment.referenceNumber}` : ""}
                    {payment.image ? (
                      <>
                        {" · "}
                        <a href={payment.image} target="_blank" rel="noreferrer">
                          Picture
                        </a>
                      </>
                    ) : null}
                  </span>
                ))}
                <RecordActions
                  disableEdit={locked}
                  disableDelete={locked}
                  onEdit={() => {
                    setEditing(item);
                    setForm(expenseToForm(item));
                  }}
                  onDelete={() => setPendingDelete(item)}
                >
                  <Link className="btn btn-ghost btn-sm" to={`/expenses/${item.id}/statement`}>
                    Statement
                  </Link>
                  {isBuilder && item.canSettle ? (
                    <Button
                      className="btn-sm"
                      disabled={isTodayClosed}
                      title={isTodayClosed ? COPY.dayClosedHint : undefined}
                      onClick={() => openPay(item)}
                    >
                      Pay
                    </Button>
                  ) : null}
                </RecordActions>
              </article>
            );
          })}
        </div>
      )}

      {isBuilder && isTodayClosed ? (
        <div className="banner">Today's book is closed, so udhar cannot be paid today.</div>
      ) : null}

      <Modal
        open={Boolean(paying)}
        title={paying ? `Pay ${paying.title}` : "Pay udhar"}
        onClose={() => setPaying(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPaying(null)}>
              Cancel
            </Button>
            <Button type="submit" form="udhar-pay-form" disabled={busy}>
              Save payment
            </Button>
          </>
        }
      >
        {paying ? (
          <form id="udhar-pay-form" className="form-grid" onSubmit={settle}>
            <Field label="Amount" hint={`Maximum ${formatMoney(paying.remainingAmount)}`}>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                max={paying.remainingAmount}
                required
                value={payForm.amount}
                onChange={(event) => setPayForm((prev) => ({ ...prev, amount: event.target.value }))}
              />
            </Field>
            <PaymentFields form={payForm} onChange={setPayForm} mode="settle" label="Pay with" />
            <Field label="Note" wide>
              <Input
                value={payForm.note}
                onChange={(event) => setPayForm((prev) => ({ ...prev, note: event.target.value }))}
                placeholder="Optional"
              />
            </Field>
          </form>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(editing)}
        title="Edit expense"
        onClose={() => {
          setEditing(null);
          setForm(emptyExpenseForm);
        }}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setEditing(null);
                setForm(emptyExpenseForm);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="expense-form"
              disabled={busy || Boolean(editing && isClosed(editing.date))}
            >
              Save
            </Button>
          </>
        }
      >
        <LockedNote show={Boolean(editing && isClosed(editing.date))} />
        <ExpenseForm
          form={form}
          onChange={setForm}
          onSubmit={saveEdit}
          projects={projects}
          vendors={vendors}
          editing
        />
      </Modal>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete expense?"
        text={COPY.deleteExpense}
        busy={busy}
        onClose={() => setPendingDelete(null)}
        onConfirm={remove}
      />
    </div>
  );
}
